import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Flex,
  Heading,
  HStack,
  Icon,
  Image,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  CheckCircleIcon,
  LockIcon,
  RepeatIcon,
  ViewIcon,
} from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import FacebookOnboarding from '../../../components/WAEmbeddedSignUp';
import { getWANumber } from '../../../hooks/whatsapp';
import { getCurentOrgDataFromStorage } from '../../../services/organization';
import { Organization } from '../../../utils/types';

const benefits = [
  { icon: CheckCircleIcon, title: 'Free monthly messages' },
  { icon: LockIcon, title: 'Encrypted & secure chats' },
  { icon: ViewIcon, title: 'Insightful chat analytics' },
  { icon: RepeatIcon, title: 'Smart automation tools' },
];

export const WhatsAppOnboarding: React.FC = () => {
  const [org, setOrg] = useState<Organization>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orgError, setOrgError] = useState<string | null>(null);
  const navigate = useNavigate();

  const MAX_RETRIES = 15;
  const RETRY_DELAY_MS = 200;

  useEffect(() => {
    let retryCount = 0;
    let isCancelled = false;

    const fetchOrgData = async () => {
      setLoading(true);
      while (retryCount < MAX_RETRIES) {
        const response = await getCurentOrgDataFromStorage();
        if (isCancelled) return;

        if (
          response?.success &&
          response.data &&
          typeof response.data === 'object'
        ) {
          setOrg(response.data);
          setLoading(false);
          return;
        }

        retryCount++;
        await new Promise((res) => setTimeout(res, RETRY_DELAY_MS));
      }

      setOrgError('Failed to load organization data after multiple attempts.');
      alert('Organization not found. Redirecting to home...');
      navigate('/home');
      setLoading(false);
    };

    fetchOrgData();

    return () => {
      isCancelled = true;
    };
  }, [navigate]);

  useEffect(() => {
    const checkConnection = async () => {
      if (!org?.id) return;

      try {
        setLoading(true);
        setError(null);

        const res = await getWANumber(org.id);
        if (res?.success && res.data.length > 0) {
          navigate(`/organization/${org.id}/inbox/whatsapp/conversations`);
        }
      } catch (e) {
        console.error('Connection check failed:', e);
        setError('Failed to check WhatsApp connection.');
      } finally {
        setLoading(false);
      }
    };

    if (org) checkConnection();
  }, [org, navigate]);

  if (loading) {
    return (
      <Flex minH="100vh" justify="center" align="center" bg="#f7f8fa" px={4}>
        <VStack spacing={4}>
          <Spinner size="xl" color="#25D366" thickness="4px" />
          <Text fontSize="sm" color="gray.500">
            Preparing your WhatsApp workspace
          </Text>
        </VStack>
      </Flex>
    );
  }

  return (
    <Box minH="100vh" bg="#f7f8fa" px={{ base: 4, md: 6 }} py={{ base: 8, md: 10 }}>
      <Flex maxW="760px" mx="auto" justify="center">
        <Box
          w="full"
          bg="white"
          border="1px solid"
          borderColor="blackAlpha.100"
          borderRadius={{ base: '24px', md: '28px' }}
          boxShadow="0 24px 60px rgba(15, 23, 32, 0.06)"
          overflow="hidden"
        >
          <VStack align="stretch" spacing={6} px={{ base: 6, md: 8 }} py={{ base: 7, md: 9 }}>
            <VStack align="center" spacing={4} textAlign="center">
              <Flex
                w="60px"
                h="60px"
                borderRadius="20px"
                bg="green.50"
                align="center"
                justify="center"
              >
                <Image
                  src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
                  alt="WhatsApp Logo"
                  boxSize="32px"
                />
              </Flex>

              <VStack spacing={3}>
                <Heading
                  fontSize={{ base: '2xl', md: '4xl' }}
                  lineHeight={{ base: 1.15, md: 1.05 }}
                  letterSpacing="-0.04em"
                  color="gray.900"
                >
                  Connect Your WhatsApp Business Account
                </Heading>
                <Text maxW="540px" fontSize={{ base: 'md', md: 'lg' }} color="gray.600" lineHeight="1.8">
                  Set up WhatsApp to message customers, automate replies, and track chats from one place.
                </Text>
                <Text fontSize="sm" color="gray.500">
                  {org?.name || 'Organization workspace'}
                </Text>
              </VStack>
            </VStack>

            {(error || orgError) && (
              <VStack align="stretch" spacing={3}>
                {error && (
                  <Alert status="error" borderRadius="16px">
                    <AlertIcon />
                    {error}
                  </Alert>
                )}
                {orgError && (
                  <Alert status="error" borderRadius="16px">
                    <AlertIcon />
                    {orgError}
                  </Alert>
                )}
              </VStack>
            )}

            <Flex
              wrap="wrap"
              justify="center"
              gap={3}
              pt={1}
            >
              {benefits.map(({ icon, title }) => (
                <HStack
                  key={title}
                  spacing={2.5}
                  px={3.5}
                  py={2.5}
                  borderRadius="999px"
                  bg="gray.50"
                  border="1px solid"
                  borderColor="blackAlpha.100"
                >
                  <Icon as={icon} boxSize={4} color="#25D366" />
                  <Text fontSize="sm" fontWeight="600" color="gray.700">
                    {title}
                  </Text>
                </HStack>
              ))}
            </Flex>

            <Box
              borderTop="1px solid"
              borderColor="blackAlpha.100"
              pt={{ base: 4, md: 5 }}
            >
              <FacebookOnboarding />
            </Box>
          </VStack>
        </Box>
      </Flex>
    </Box>
  );
};
